import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com'

export interface TaleFromTheRail {
  id: string
  title: string
  body: string
  scribe: string // Instead of "Author"
  tags: string[]
  images?: string[] // Optional photos for the tale
  createdAt: string
  updatedAt: string
}

export interface RailbirdGame {
  id: string
  name: string
  description: string
  rules: string
  maxPlayers: number
  talesFromTheRail: TaleFromTheRail[] // Corrected property name
  images?: string[] // Optional photos for the game
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateRailbirdGameData {
  name: string
  description: string
  rules: string
  maxPlayers: number
  images?: string[] // Optional photos for the game
}

export interface UpdateRailbirdGameData {
  name?: string
  description?: string
  rules?: string
  maxPlayers?: number
  images?: string[] // Optional photos for the game
}

export interface CreateTaleData {
  title: string
  body: string
  scribe: string
  tags: string[]
  images?: string[] // Optional photos for the tale
}

export interface UpdateTaleData {
  title?: string
  body?: string
  scribe?: string
  tags?: string[]
  images?: string[] // Optional photos for the tale
}

export interface RailbirdGamesResponse {
  games: RailbirdGame[]
  count: number
  hasMore: boolean
  nextKey?: string
}

export interface RailbirdGamesSearchParams {
  limit?: number
  lastKey?: string
  search?: string
  tags?: string[]
}

class RailbirdGamesApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null;

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently;
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.');
    }
    try {
      const token = await this.getAccessTokenSilently();
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
    } catch (error) {
      throw new Error('Authentication failed');
    }
  }

  // Get all games with pagination and search
  async getGames(getAccessTokenSilently: () => Promise<string>, options?: RailbirdGamesSearchParams): Promise<RailbirdGamesResponse> {
    const token = await getAccessTokenSilently()
    
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.lastKey) params.append('lastKey', options.lastKey)
    if (options?.search) params.append('search', options.search)
    if (options?.tags && options.tags.length > 0) {
      params.append('tags', options.tags.join(','))
    }
    
    const url = `${API_BASE_URL}/v1/railbird-games${params.toString() ? `?${params}` : ''}`
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch railbird games: ${response.status}`)
    }

    return response.json()
  }

  // Get a single game
  async getGame(id: string, getAccessTokenSilently: () => Promise<string>): Promise<RailbirdGame> {
    const token = await getAccessTokenSilently()
    
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch railbird game: ${response.status}`)
    }

    return response.json()
  }

  // Create a new game
  async createGame(data: CreateRailbirdGameData): Promise<{ id: string, message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create railbird game: ${response.statusText}`);
    }
    return response.json();
  }

  // Update a game
  async updateGame(id: string, data: UpdateRailbirdGameData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update railbird game: ${response.statusText}`);
    }
    return response.json();
  }

  // Delete a game
  async deleteGame(id: string): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${id}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to delete railbird game: ${response.statusText}`);
    }
    return response.json();
  }

  // Create a tale
  async createTale(gameId: string, data: CreateTaleData): Promise<{ id: string, message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${gameId}/tales`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create tale: ${response.statusText}`);
    }
    return response.json();
  }

  // Update a tale
  async updateTale(gameId: string, taleId: string, data: UpdateTaleData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${gameId}/tales/${taleId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update tale: ${response.statusText}`);
    }
    return response.json();
  }

  // Delete a tale
  async deleteTale(gameId: string, taleId: string): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-games/${gameId}/tales/${taleId}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to delete tale: ${response.statusText}`);
    }
    return response.json();
  }
}

export const railbirdGamesApi = new RailbirdGamesApi();

export const useRailbirdGamesApi = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0();
  useEffect(() => {
    if (!isLoading) {
      railbirdGamesApi.setAuth(getAccessTokenSilently);
    }
  }, [isLoading, getAccessTokenSilently]);
  return railbirdGamesApi;
};